import { useCallback, useEffect, useState } from "react";
import { api } from "../../api.js";
import GoogleSignIn from "../../components/GoogleSignIn.jsx";
import { Button, Eyebrow, Field, Notice } from "../../components/ui.jsx";
import { PublicHeader } from "./PublicChrome.jsx";

export default function SignupPage({ onAccount }) {
  const [form, setForm] = useState(() => ({
    name: "", email: new URLSearchParams(window.location.search).get("email") || "", password: "", confirmation: "",
  }));
  const [googleCredential, setGoogleCredential] = useState("");
  const [googlePassword, setGooglePassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inviteToken = new URLSearchParams(window.location.search).get("invite") || null;
  useEffect(() => { document.title = "Create your account · RevPal"; }, []);

  const googleError = useCallback((requestError) => {
    setError(requestError.message);
  }, []);

  const continueWithGoogle = useCallback(async (credential) => {
    setError("");
    setBusy(true);
    try {
      const data = await api.googleSignIn(credential, { inviteToken });
      onAccount(data.account);
    } catch (requestError) {
      if (requestError.code === "google_link_confirmation_required") {
        setGoogleCredential(credential);
        setGooglePassword("");
      } else {
        setError(requestError.message);
      }
    } finally {
      setBusy(false);
    }
  }, [inviteToken, onAccount]);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(event) {
    event.preventDefault();
    setError("");
    if (googleCredential) {
      setBusy(true);
      try {
        const data = await api.googleSignIn(googleCredential, { password: googlePassword, inviteToken });
        onAccount(data.account);
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (form.password.length < 12) {
      setError("Use at least 12 characters for your password.");
      return;
    }
    if (form.password !== form.confirmation) {
      setError("The passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const data = await api.signup({ name: form.name, email: form.email, password: form.password });
      onAccount(data.account);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicHeader current="signup" />
      <main className="mx-auto grid min-h-[calc(100vh-80px)] w-full max-w-[1440px] lg:grid-cols-[minmax(340px,.78fr)_minmax(500px,1.22fr)]">
        <aside className="grid content-between gap-14 border-b border-border bg-background p-8 lg:border-b-0 lg:border-r lg:p-[clamp(40px,6vw,88px)]">
          <div className="grid gap-6"><Eyebrow>Begin with your identity</Eyebrow><h1 className="max-w-[14ch] text-[clamp(2.4rem,4vw,3.5rem)] font-extrabold leading-[1.04] tracking-[-0.035em]">Your account comes before the org chart.</h1><p className="max-w-[48ch] text-sm leading-7 text-muted-foreground">Create one RevPal identity. After signing in, choose an organization you already belong to, request access to a matching organization, or create a new one.</p></div>
          <ol className="grid rounded-xl bg-card px-5 py-1 text-xs shadow-soft">
            <li className="grid grid-cols-[32px_1fr] gap-3 border-b border-neutral-100 py-4 text-foreground"><span className="font-bold text-primary">01</span><span className="grid gap-1"><strong>Your account</strong><small className="font-normal text-muted-foreground">Identity and secure access</small></span></li>
            <li className="grid grid-cols-[32px_1fr] gap-3 py-4 text-muted-foreground"><span>02</span><span className="grid gap-1"><strong>Choose an organization</strong><small className="font-normal">Open, request, or create</small></span></li>
          </ol>
        </aside>

        <section className="grid place-items-center px-5 py-16 sm:px-8">
          <form className="grid w-full max-w-[520px] gap-6 rounded-xl bg-card p-8 shadow-soft" onSubmit={submit}>
            <div className="grid gap-3"><Eyebrow>Account setup</Eyebrow><h2 className="text-[30px] font-bold tracking-[-0.02em]">Create your RevPal login</h2><p className="text-sm text-muted-foreground">Already registered? <a className="font-bold text-primary hover:text-primary-600" href="/login">Sign in</a>.</p></div>
            {!googleCredential && <GoogleSignIn onCredential={continueWithGoogle} onError={googleError} />}
            {error && <Notice>{error}</Notice>}
            {googleCredential ? (
              <div className="grid gap-5 rounded-xl border border-border bg-muted p-5">
                <div className="grid gap-2"><Eyebrow>One-time account check</Eyebrow><p className="text-sm leading-6 text-muted-foreground">A RevPal account already uses this email. Confirm its current password once, then you can use Google from now on.</p></div>
                <Field label="Current RevPal password" type="password" value={googlePassword} onChange={(event) => setGooglePassword(event.target.value)} autoComplete="current-password" autoFocus required />
                <div className="flex flex-wrap justify-end gap-2"><Button type="button" onClick={() => { setGoogleCredential(""); setGooglePassword(""); setError(""); }}>Cancel</Button><Button variant="primary" type="submit" disabled={busy}>{busy ? "Linking…" : "Link Google account"}</Button></div>
              </div>
            ) : (
              <>
                <Field label="Your name" value={form.name} onChange={(event) => update("name", event.target.value)} autoComplete="name" maxLength={120} autoFocus required />
                <Field label="Work email" type="email" value={form.email} onChange={(event) => update("email", event.target.value)} autoComplete="email" maxLength={254} required />
                <div className="grid gap-5 sm:grid-cols-2"><Field label="Password" hint="12+ characters" type="password" value={form.password} onChange={(event) => update("password", event.target.value)} autoComplete="new-password" minLength={12} required /><Field label="Confirm password" type="password" value={form.confirmation} onChange={(event) => update("confirmation", event.target.value)} autoComplete="new-password" minLength={12} required /></div>
                <p className="text-[11px] leading-5 text-muted-foreground">Organization access is handled separately. Matching an email domain lets you request access; it never grants access automatically.</p>
                <Button variant="primary" type="submit" className="w-full sm:w-auto sm:justify-self-end" disabled={busy}>{busy ? "Creating account…" : "Continue to organizations →"}</Button>
              </>
            )}
          </form>
        </section>
      </main>
    </div>
  );
}
